import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getCurrentShop, getCurrentUser } from "@/lib/auth";
import { getTransfer, sendTransfer, cancelTransfer } from "@/lib/api";
import { Loader2, Send, XCircle, Truck, Package, Calendar, User, FileText } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { exportToExcel } from "@/lib/exportToExcel";

const STATUS_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  draft: { label: "Borrador", variant: "secondary" },
  in_transit: { label: "En tránsito", variant: "default" },
  completed: { label: "Completada", variant: "outline" },
  cancelled: { label: "Cancelada", variant: "destructive" },
  issue: { label: "Con incidencia", variant: "destructive" },
};

export function TransferDetailDialog({
  transferId, show, onClose, onUpdate,
}: {
  transferId: string | null;
  show: boolean;
  onClose: () => void;
  onUpdate: () => void;
}) {
  const currentShop = getCurrentShop()!;
  const currentUser = getCurrentUser();
  const [transfer, setTransfer] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (show && transferId) {
      setLoading(true);
      getTransfer(currentShop.id, transferId)
        .then(setTransfer)
        .catch(() => setTransfer(null))
        .finally(() => setLoading(false));
    } else {
      setTransfer(null);
    }
  }, [show, transferId, currentShop.id]);

  const handleSend = async () => {
    if (!transferId) return;
    setActionLoading(true);
    try {
      await sendTransfer(currentShop.id, transferId);
      toast({ title: "Transferencia enviada" });
      onUpdate();
      onClose();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!transferId) return;
    setActionLoading(true);
    try {
      await cancelTransfer(currentShop.id, transferId);
      toast({ title: "Transferencia cancelada" });
      onUpdate();
      onClose();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleExportExcel = async () => {
    if (!transfer?.items?.length) return;
    const formatted = transfer.items.map((item: any, i: number) => ({
      "#": i + 1,
      "Producto": item.product?.name || item.productName || "—",
      "Enviado": item.quantitySent,
      "Recibido": item.quantityReceived ?? "—",
      "Diferencia": (() => {
        if (item.quantityReceived === null || item.quantityReceived === undefined) return "—";
        const diff = item.quantityReceived - item.quantitySent;
        return diff > 0 ? `+${diff}` : diff === 0 ? "0" : `${diff}`;
      })(),
      "Costo Und": `$${parseFloat(item.unitCost || "0").toFixed(2)}`,
    }));
    await exportToExcel(
      formatted,
      `transferencia_${transfer.sequential}_${new Date().toISOString().split("T")[0]}`,
      "Transferencia",
    );
  };

  const sc = STATUS_BADGE[transfer?.status] || { label: transfer?.status || "", variant: "outline" };
  const isOrigin = transfer?.shopOriginId === currentShop.id;

  return (
    <Dialog open={show} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Transferencia {transfer?.sequential || ""}
            {transfer && <Badge variant={sc.variant} className="hidden sm:inline-flex">{sc.label}</Badge>}
          </DialogTitle>
        </DialogHeader>

        {transfer && !loading && (
          <div className="sm:hidden -mt-2 mb-2">
            <Badge variant={sc.variant}>{sc.label}</Badge>
          </div>
        )}

        {loading ? (
          <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : !transfer ? (
          <p className="py-8 text-center text-muted-foreground">Transferencia no encontrada</p>
        ) : (
          <div className="space-y-6 py-4 min-w-0">
            {/* Info grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-2 text-sm"><Truck className="h-4 w-4 text-muted-foreground" /> <span className="font-medium">Origen:</span> {transfer.shopOrigin?.name || transfer.shopOriginId}</div>
              <div className="flex items-center gap-2 text-sm"><Package className="h-4 w-4 text-muted-foreground" /> <span className="font-medium">Destino:</span> {transfer.shopDestination?.name || transfer.shopDestinationId}</div>
              <div className="flex items-center gap-2 text-sm"><Calendar className="h-4 w-4 text-muted-foreground" /> <span className="font-medium">Creada:</span> {transfer.createdAt ? new Date(transfer.createdAt).toLocaleDateString("es-CU") : "—"}</div>
              {transfer.sentBy && <div className="flex items-center gap-2 text-sm"><User className="h-4 w-4 text-muted-foreground" /> <span className="font-medium">Enviada por:</span> {transfer.sentBy.fullName || transfer.sentBy.username}</div>}
              {transfer.receivedBy && <div className="flex items-center gap-2 text-sm"><User className="h-4 w-4 text-muted-foreground" /> <span className="font-medium">Recibida por:</span> {transfer.receivedBy.fullName || transfer.receivedBy.username}</div>}
              {transfer.notes && <div className="col-span-full text-sm"><span className="font-medium">Notas:</span> {transfer.notes}</div>}
            </div>

            {/* Tracking timeline */}
            {transfer.tracking && transfer.tracking.length > 0 && (
              <div>
                <h4 className="font-medium text-sm mb-2">Trazabilidad</h4>
                <div className="space-y-2">
                  {transfer.tracking.map((t: any, i: number) => (
                    <div key={t.id} className="flex gap-3 text-sm">
                      <div className="flex flex-col items-center">
                        <div className="w-2 h-2 rounded-full bg-primary mt-1.5" />
                        {i < transfer.tracking.length - 1 && <div className="w-px h-4 bg-border" />}
                      </div>
                      <div>
                        <p className="font-medium">{t.toStatus === "draft" ? "Borrador creado" : t.toStatus === "in_transit" ? "Enviada" : t.toStatus === "completed" ? "Recibida" : t.toStatus === "cancelled" ? "Cancelada" : t.toStatus === "issue" ? "Incidencia reportada" : t.toStatus}</p>
                        {t.notes && <p className="text-muted-foreground">{t.notes}</p>}
                        <p className="text-xs text-muted-foreground">{t.createdAt ? new Date(t.createdAt).toLocaleString("es-CU") : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Product table */}
            <div>
              <h4 className="font-medium text-sm mb-2">Productos</h4>
              <div className="border rounded-lg overflow-x-auto">
                <table className="min-w-[550px] text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="p-1 sm:p-2 text-left">Producto</th>
                      <th className="p-1 sm:p-2 text-right">Enviado</th>
                      <th className="p-1 sm:p-2 text-right">Recibido</th>
                      <th className="p-1 sm:p-2 text-right">Diferencia</th>
                      <th className="p-1 sm:p-2 text-right">Costo Und</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transfer.items?.map((item: any) => {
                      const diff = (item.quantityReceived ?? item.quantitySent) - item.quantitySent;
                      return (
                        <tr key={item.id} className="border-b">
                          <td className="p-1 sm:p-2 font-medium break-words max-w-[120px] sm:max-w-none">{item.product?.name || item.productName || "—"}</td>
                          <td className="p-1 sm:p-2 text-right">{item.quantitySent}</td>
                          <td className="p-1 sm:p-2 text-right">{item.quantityReceived ?? "—"}</td>
                          <td className={`p-1 sm:p-2 text-right ${diff < 0 ? "text-destructive font-medium" : diff > 0 ? "text-green-600" : ""}`}>
                            {item.quantityReceived !== undefined && item.quantityReceived !== null ? (diff > 0 ? `+${diff}` : diff === 0 ? "0" : diff) : "—"}
                          </td>
                          <td className="p-1 sm:p-2 text-right">${parseFloat(item.unitCost || "0").toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Export */}
            <div className="flex justify-start gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportExcel}
                disabled={!transfer?.items?.length}
              >
                <FileText className="mr-2 h-4 w-4 text-green-600" />
                Exportar Excel
              </Button>
            </div>

            {/* Actions */}
            {isOrigin && transfer.status === "draft" && (
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button onClick={handleSend} disabled={actionLoading} className="flex-1">
                  {actionLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enviando...</> : <><Send className="mr-2 h-4 w-4" /> Enviar Transferencia</>}
                </Button>
                <Button variant="outline" onClick={handleCancel} disabled={actionLoading}>
                  <XCircle className="mr-2 h-4 w-4" /> Cancelar
                </Button>
              </div>
            )}
            {isOrigin && transfer.status === "in_transit" && (
              <Button variant="destructive" onClick={handleCancel} disabled={actionLoading}>
                {actionLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cancelando...</> : <><XCircle className="mr-2 h-4 w-4" /> Cancelar Transferencia</>}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
